// src/services/EvidenceDataService.js - UPDATED with inherited exhibit numbers for filtering
/**
 * Centralized service for managing evidence data consistency
 * Ensures case numbers, exhibit identifiers, and page ranges are consistent across all components
 * SINGLE SOURCE OF TRUTH for all display calculations
 */
class EvidenceDataService {
  /**
   * Get exhibit identifier for a document based on stamp configuration
   * @param {number} documentIndex - Zero-based index of the document (0, 1, 2...)
   * @param {Object} stampConfig - Stamp configuration object
   * @returns {string} - The exhibit identifier (A, B, C or 1, 2, 3, etc.)
   */
  static getExhibitIdentifier(documentIndex, stampConfig = {}) {
    // Default configuration
    const config = {
      exhibitType: 'letters',
      startingValue: 'A',
      ...stampConfig
    };

    if (config.exhibitType === 'letters') {
      return this.getLetterIdentifier(documentIndex, config.startingValue);
    } else {
      return this.getNumberIdentifier(documentIndex, config.startingValue);
    }
  }

  /**
   * Get letter-based exhibit identifier (A, B, C, AA, AB, etc.)
   * @param {number} index - Zero-based document index
   * @param {string} startingLetter - Starting letter (default 'A')
   * @returns {string} - Letter identifier
   */
  static getLetterIdentifier(index, startingLetter = 'A') {
    const startCharCode = startingLetter.charCodeAt(0);
    const targetIndex = index;
    
    if (targetIndex < 26) {
      // Single letter: A, B, C... Z
      return String.fromCharCode(startCharCode + targetIndex);
    } else {
      // Double letter: AA, AB, AC...
      const firstLetterIndex = Math.floor(targetIndex / 26);
      const secondLetterIndex = targetIndex % 26;
      
      const firstLetter = String.fromCharCode(startCharCode + firstLetterIndex - 1);
      const secondLetter = String.fromCharCode(startCharCode + secondLetterIndex);
      
      return firstLetter + secondLetter;
    }
  }

  /**
   * Get number-based exhibit identifier
   * @param {number} index - Zero-based document index
   * @param {string|number} startingNumber - Starting number (default 1)
   * @returns {string} - Number identifier
   */
  static getNumberIdentifier(index, startingNumber = '1') {
    const startNum = parseInt(startingNumber) || 1;
    return (startNum + index).toString();
  }

  /**
   * Get properly formatted case number for display
   * @param {Object} caseData - Case object with name and caseNumber
   * @returns {string} - Formatted case number or "---" for empty cases
   */
  static getCaseNumber(caseData) {
    if (!caseData) return '---';
    
    // If there's a case number and it's not empty/whitespace, use it
    if (caseData.caseNumber && caseData.caseNumber.trim()) {
      return caseData.caseNumber.trim();
    }
    
    // For empty case numbers, show "---" instead of case name
    return '---';
  }

  /**
   * Get party type text for display
   * @param {Object} stampConfig - Stamp configuration
   * @returns {string} - Party type text for display
   */
  static getPartyTypeText(stampConfig = {}) {
    const config = this.validateStampConfig(stampConfig);
    
    if (config.partyType === 'other' && config.customPartyType) {
      return config.customPartyType;
    }
    
    const partyTypeMap = {
      'petitioner': 'Petitioner',
      'respondent': 'Respondent',
      'plaintiff': 'Plaintiff',
      'defendant': 'Defendant'
    };
    
    return partyTypeMap[config.partyType] || 'Petitioner';
  }

  /**
   * Get display name for document order column (should match exhibit identifier)
   * @param {number} documentIndex - Zero-based index of the document
   * @param {Object} stampConfig - Stamp configuration
   * @returns {string} - Display value for order column
   */
  static getDocumentOrderDisplay(documentIndex, stampConfig) {
    return this.getExhibitIdentifier(documentIndex, stampConfig);
  }

  /**
   * Get page range string for document
   * @param {number} startPage - Starting page number in combined document
   * @param {number} pageCount - Number of pages in this document
   * @param {boolean} forIndex - Whether this is for the index table (always shows range format)
   * @returns {string} - Page range (e.g., "5" or "5-7"), or "-" if pageCount is invalid
   */
  static getPageRangeDisplay(startPage, pageCount = 1, forIndex = false) {
    // FIXED: Handle undefined or invalid pageCount (happens when document is still processing)
    if (!pageCount || pageCount < 1 || isNaN(pageCount)) {
      return '-';
    }
    
    if (pageCount <= 1) {
      // For single pages, index always shows "5-5" format, table can show just "5"
      return forIndex ? `${startPage}-${startPage}` : startPage.toString();
    } else {
      // Multiple pages always show range format
      const endPage = startPage + pageCount - 1;
      return `${startPage}-${endPage}`;
    }
  }

  /**
   * SINGLE SOURCE OF TRUTH: Get complete document metadata for display
   * FILTERS OUT INDEX DOCUMENTS and calculates all display values consistently
   * @param {Array} documents - Array of document objects (may include index documents)
   * @param {Object} stampConfig - Stamp configuration
   * @param {Object} caseData - Case information
   * @returns {Array} - Enhanced document array with consistent display data (index docs included with special handling)
   */
  static getDocumentDisplayData(documents, stampConfig, caseData) {
    // Start at page 1 (not page 2) - the index will be page 0, first doc is page 1
    let pageCounter = 1;
    
    // Track the document index for exhibit numbering (excludes index documents)
    let exhibitIndex = 0;

    console.log('📊 getDocumentDisplayData called with', documents.length, 'documents');
    
    const result = documents.map((doc, arrayIndex) => {
      // Special handling for index documents
      if (doc.isIndex || doc.id === 'INDEX_DOCUMENT') {
        return {
          ...doc,
          exhibitId: '-',  // Index doesn't get an exhibit number
          caseNumber: this.getCaseNumber(caseData),
          pageRangeTable: '-',  // Index doesn't show in page count
          pageRangeIndex: '-',
          startPage: 0,  // Index is page 0
          documentIndex: -1  // Not a real document
        };
      }

      // For regular documents, calculate everything
      const exhibitId = this.getExhibitIdentifier(exhibitIndex, stampConfig);
      const pageCount = doc.pageCount || 1;
      const pageRangeTable = this.getPageRangeDisplay(pageCounter, pageCount, false);
      const pageRangeIndex = this.getPageRangeDisplay(pageCounter, pageCount, true);
      const caseNumber = this.getCaseNumber(caseData);

      const docResult = {
        ...doc,
        exhibitId,
        caseNumber,
        pageRangeTable,
        pageRangeIndex,
        startPage: pageCounter,
        documentIndex: exhibitIndex
      };

      // Increment counters for next document
      pageCounter += pageCount;
      exhibitIndex++;
      
      return docResult;
    });

    console.log('📊 getDocumentDisplayData result:', result.map(d => ({
      id: d.id,
      exhibitId: d.exhibitId,
      documentIndex: d.documentIndex
    })));

    return result;
  }

  /**
   * Get filtered document list (no index documents) with display data
   * Use this when you need just the evidence documents without the index
   * FILTERS OUT documents that are still processing (no pageCount yet)
   * @param {Array} documents - Array of document objects
   * @param {Object} stampConfig - Stamp configuration
   * @param {Object} caseData - Case information
   * @returns {Array} - Filtered and enhanced document array
   */
  static getFilteredDocumentDisplayData(documents, stampConfig, caseData) {
    console.log('🔍 getFilteredDocumentDisplayData called with', documents.length, 'documents');
    
    // Filter out index documents AND documents without pageCount (still processing)
    const filteredDocuments = documents.filter(doc => {
      const isIndexDoc = doc.isIndex || doc.id === 'INDEX_DOCUMENT';
      const pageCount = doc.pageCount;
      const hasValidPageCount = pageCount && pageCount > 0 && !isNaN(pageCount);
      
      console.log(`  Checking doc ${doc.id}:`, {
        isIndexDoc,
        pageCount,
        hasValidPageCount,
        willInclude: !isIndexDoc && hasValidPageCount
      });
      
      // Only include if it's NOT an index doc AND it HAS a valid page count
      return !isIndexDoc && hasValidPageCount;
    });
    
    console.log('🔍 After filtering:', filteredDocuments.length, 'documents remain');
    
    // Then get display data (which will start counting from 0)
    return this.getDocumentDisplayData(filteredDocuments, stampConfig, caseData);
  }

  /**
   * NEW: Get display data for filtered documents while maintaining exhibit numbers from the full array
   * This is used when viewing document sets or search results - the exhibit numbers should match
   * the document's position in the FULL, unfiltered array, not the filtered subset
   * @param {Array} fullDocuments - Full array of all document objects (unfiltered)
   * @param {Array} filteredDocuments - Subset of documents to display
   * @param {Object} stampConfig - Stamp configuration
   * @param {Object} caseData - Case information
   * @returns {Array} - Filtered documents with inherited exhibit numbers from full array
   */
  static getFilteredDisplayDataWithInheritedNumbers(fullDocuments, filteredDocuments, stampConfig, caseData) {
    console.log('🔢 getFilteredDisplayDataWithInheritedNumbers called');
    console.log('  Full documents:', fullDocuments.length);
    console.log('  Filtered documents:', filteredDocuments.length);
    
    // First, calculate display data for the FULL array to get the correct exhibit numbers
    const fullDisplayData = this.getDocumentDisplayData(fullDocuments, stampConfig, caseData);
    
    // Create a map of document ID -> exhibit data from the full array
    const exhibitMap = new Map();
    fullDisplayData.forEach(doc => {
      if (!doc.isIndex && doc.id !== 'INDEX_DOCUMENT') {
        exhibitMap.set(doc.id, {
          exhibitId: doc.exhibitId,
          documentIndex: doc.documentIndex
        });
      }
    });
    
    console.log('  Created exhibit map with', exhibitMap.size, 'entries');
    
    // Now process the filtered documents, inheriting their exhibit data from the full array
    // But recalculating page ranges based on the filtered set (page numbering restarts)
    let pageCounter = 1; // Start at page 1 for the filtered set's page numbering
    
    const result = filteredDocuments.map((doc) => {
      // Special handling for index documents
      if (doc.isIndex || doc.id === 'INDEX_DOCUMENT') {
        return {
          ...doc,
          exhibitId: '-',
          caseNumber: this.getCaseNumber(caseData),
          pageRangeTable: '-',
          pageRangeIndex: '-',
          startPage: 0,
          documentIndex: -1
        };
      }

      // Get the inherited exhibit data from the full array
      const inheritedData = exhibitMap.get(doc.id);
      
      if (!inheritedData) {
        console.warn(`Document ${doc.id} not found in full array`);
        // Fallback to basic data
        return {
          ...doc,
          exhibitId: '?',
          caseNumber: this.getCaseNumber(caseData),
          pageRangeTable: '-',
          pageRangeIndex: '-',
          startPage: pageCounter,
          documentIndex: -1
        };
      }

      // Use inherited exhibit data but calculate new page ranges for the filtered view
      const pageCount = doc.pageCount || 1;
      const pageRangeTable = this.getPageRangeDisplay(pageCounter, pageCount, false);
      const pageRangeIndex = this.getPageRangeDisplay(pageCounter, pageCount, true);
      const caseNumber = this.getCaseNumber(caseData);

      const docResult = {
        ...doc,
        exhibitId: inheritedData.exhibitId, // Inherited from full array
        caseNumber,
        pageRangeTable, // Recalculated for filtered view (restarted numbering)
        pageRangeIndex, // Recalculated for filtered view (restarted numbering)
        startPage: pageCounter, // Recalculated for filtered view (restarted numbering)
        documentIndex: inheritedData.documentIndex // Inherited from full array
      };

      // Increment page counter for next document in filtered set
      pageCounter += pageCount;
      
      return docResult;
    });

    console.log('🔢 Inherited numbers result:', result.map(d => ({
      id: d.id,
      exhibitId: d.exhibitId,
      startPage: d.startPage
    })));

    return result;
  }

  /**
   * Get stamp configuration data for a specific document
   * @param {number} documentIndex - Zero-based document index
   * @param {Object} stampConfig - Stamp configuration
   * @param {Object} caseData - Case information
   * @returns {Object} - Complete stamp data for rendering
   */
  static getStampData(documentIndex, stampConfig, caseData) {
    return {
      exhibitNumber: this.getExhibitIdentifier(documentIndex, stampConfig),
      caseNumber: this.getCaseNumber(caseData),
      config: stampConfig,
      documentIndex
    };
  }

  /**
   * Validate stamp configuration
   * @param {Object} stampConfig - Configuration to validate
   * @returns {Object} - Validated configuration with defaults
   */
  static validateStampConfig(stampConfig = {}) {
    const defaults = {
      partyType: 'petitioner',
      customPartyType: '',
      exhibitType: 'letters',
      startingValue: 'A',
      color: '#4A93CE',
      transparency: 'low'
    };

    const validated = { ...defaults, ...stampConfig };

    // Validate party type
    const validPartyTypes = ['petitioner', 'respondent', 'plaintiff', 'defendant', 'other'];
    if (!validPartyTypes.includes(validated.partyType)) {
      validated.partyType = 'petitioner';
    }

    // Validate exhibit type and starting value
    if (validated.exhibitType === 'letters') {
      // Validate starting letter
      if (!validated.startingValue || !/^[A-Z]$/.test(validated.startingValue)) {
        validated.startingValue = 'A';
      }
    } else if (validated.exhibitType === 'numbers') {
      // Validate starting number
      const num = parseInt(validated.startingValue);
      if (isNaN(num) || num < 1 || num > 9999) {
        validated.startingValue = '1';
      } else {
        validated.startingValue = num.toString();
      }
    } else {
      // Invalid exhibit type, default to letters
      validated.exhibitType = 'letters';
      validated.startingValue = 'A';
    }

    // Validate color
    if (!validated.color || !/^#[0-9A-Fa-f]{6}$/.test(validated.color)) {
      validated.color = '#4A93CE';
    }

    // Validate transparency
    const validTransparencyLevels = ['none', 'low', 'high', 'full'];
    if (!validated.transparency || !validTransparencyLevels.includes(validated.transparency)) {
      validated.transparency = 'none';
    }

    // Validate custom party type length
    if (validated.customPartyType && validated.customPartyType.length > 15) {
      validated.customPartyType = validated.customPartyType.substring(0, 15);
    }
    
    const transparencyMap = {
      'none': 1.0,   // 100% opacity (current default)
      'low': 0.65,    // 65% opacity  
      'high': 0.35,   // 35% opacity
      'full': 0.0    // 0% opacity (completely transparent)
    };

    const mappedOpacity = transparencyMap[validated.transparency];
    validated.opacity = mappedOpacity !== undefined ? mappedOpacity : transparencyMap['low'];

    return validated;
  }
}

export default EvidenceDataService;